import { Injectable } from '@angular/core';
import { FirestoreService } from '../firestore/firestore.service';
import { Blog } from '../../shared/datamodel'
import { StorageService } from '../storage/storage.service';
import * as moment from 'dayjs';
import { firstValueFrom, Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
/*
  Generated class for the BlogService provider.

  See https://angular.io/guide/dependency-injection for more info on providers
  and Angular DI.
*/
@Injectable({providedIn: 'root'})
export class BlogService {

  constructor(
    private db: FirestoreService,
    private blogstorage: StorageService,
  ) {
    // console.log('Hello BlogService Provider');
  }

  public getBlogbyShortId(shortid): Observable<Blog>{
    return this.db.colWithIds$('blog', ref => ref.where('shortid', '==', shortid))
      .pipe(
        switchMap(blogs => {
          const blog = blogs[0] as Blog;
          if (!blog) return of(null);
          if (blog.createdAt) blog.createdAt = blog.createdAt.toDate();
          if (blog.updatedAt) blog.updatedAt = blog.updatedAt.toDate();
          if (!blog.contextText) {
            return this.html2text(blog.content)
              .pipe(map((contextText: string) => {
                blog.contextText = contextText;
                blog.JSONLDBlog = this.JSONLDBlog(blog);
                return blog;
              }));
          } else {
            blog.JSONLDBlog = this.JSONLDBlog(blog);
            return of(blog);
          }
        }),
      );
  }

  public getBlogbyId(id): Observable<Blog>{
    return this.db.docWithIds$(`blog/${id}`).pipe(map((blog:Blog) => {
      if (blog.createdAt) blog.createdAt = blog.createdAt.toDate();
      if (blog.updatedAt) blog.updatedAt = blog.updatedAt.toDate();
      blog.JSONLDBlog = this.JSONLDBlog(blog);
      return blog as Blog;
    }));
  }

  public fileType(filename) {
    const ext = filename.substring(filename.lastIndexOf('.')+1);
    const image = ["jpg", "png", "gif", "jpeg", "webp"];
    const video = ["mp4", "mkv", "avi", "mpg"];

    if (image.indexOf(ext) > -1 ) return "image";
    else if (video.indexOf(ext) > -1 ) return "video";
    else return "image"
  }

  public switchDraftOnOff (blogId, draftValue) {
    return this.db.doc(`blog/${blogId}`).ref.update({draft: draftValue});
  }


  public deleteBlog(blog){
    const dbPromises = [];
    const blogref = this.db.doc(`blog/${blog.id}`)

    if (blog.media && blog.media.list) {
      blog.media.list.forEach(file => {
        if (file.ref)
        dbPromises.push(this.blogstorage.deleteFile(file.ref).catch())
      })
    }
    dbPromises.push(blogref.ref.delete().catch());

    return Promise.all(dbPromises);
  }

  public html2text(html) {
    return this.db.post("/web/blogs/html2text", {content: html})
      .pipe(map((result:any) => {
        console.log("html2text", result);
        return result.contextText;
      }));
  }

  public updateBlog(blog){
    // Update the existing blog
    console.log("updateBlog", blog);
    const currentTime = this.db.timestamp;
    blog.updatedAt = currentTime;

    if (blog.id) {
      const blogref = this.db.doc(`blog/${blog.id}`)
      const blogId = blog.id;
      //Removing the id from the object to not create the property in addition to the firestore ID
      delete blog.id;

      return blogref.ref
        .set(blog, {merge: true})
        .then(ret => {
          return firstValueFrom(this.db.post("/web/blogs/updatecache", { blogId, roles: ['admin', 'blogeditor'] }))
          .then(() => ret)
        })
        .catch(err => {return err})
    }
    //Or create a new one
    else {
      const blogid = moment().format("YYYY-MM-DD-HH-mm-ss");
      const blogref = this.db.doc(`blog/${blogid}`)
      if (!blog.createdAt) blog.createdAt = currentTime;
      blog.updatedAt = currentTime;
      return blogref.ref
        .set(blog)
        .then(() => {
          return firstValueFrom(this.db.post("/web/blogs/updatecache", { blogId: blogid, roles: ['admin', 'blogeditor'] }))
          .then(() => ({id: blogid, ...blog}))
        })
        .catch(err => {return err})
    }
  }

  public getBlogs(page, auth: Observable<boolean>, tags: string[], author: string, draft: boolean): Observable<{blogs: Blog[], maxpages: number, nextpage: number, page: number}>{
    return this.db.post<{blogs: Blog[], maxpages: number, nextpage: number, page: number}>("/web/blogs", {page, tags, author, draft})
    .pipe(
      switchMap(result => auth.pipe(map(isIt => ({blogs: result, isIt})))),
      map(result => {
        if (result?.blogs?.blogs) {
          result.blogs.blogs.forEach(blog => {
            blog.JSONLDBlog = this.JSONLDBlog(blog);
          });
          if (!result.isIt) {
            result.blogs.blogs = result.blogs.blogs.filter(blog => !blog.communityid || (blog.communityid && blog.communityid === 'No Specific Community'));
          }
          // console.log("getBlogs Blogs", result.blogs);
          return result.blogs;
        } else {
          return {blogs: [], maxpages: 0, nextpage: 0, page: 0};
        }
      })
    )
  }

  public getRelatedBlogs(relatedBy, rawRelated, nBlogs): Observable<Blog[]> {
      return this.db.post<Blog[]>("/web/relatedblogs", {relatedBy, related: rawRelated, nBlogs: parseInt(nBlogs)});
  }

  public getBlog(shortId): Observable<Blog> {
    return this.db.get<Blog>(`/web/blog/${shortId}`);
}

  public searchBlogs(query): Observable<Blog[]> {
    return this.db.post<Blog[]>("/web/blogs/search", {query});
  }

  public getFeaturedBlogbyCommunity(communityid) {
    return this.db.colWithIds$('blog', ref => ref
      .where('communityid', '==', communityid)
      .where('featured', '==', true)
      .where('draft', '==', false)
    ).pipe(map(r => {
      if (r && r.length > 0) {
        const blog = r[0] as Blog;
        if (blog.createdAt) blog.createdAt = blog.createdAt.toDate();
        if (blog.updatedAt) blog.updatedAt = blog.updatedAt.toDate();
        blog.JSONLDBlog = this.JSONLDBlog(blog);
        return blog;
      } else return null
    }))
  }

  public JSONLDBlog(blog) {
    let image = "https://www.nucamp.co/assets/imgs/reviews/badge-nucamp-number1.webp";
    let imageIndex = -1;
    if (blog?.media?.list) imageIndex = blog.media.list.findIndex(m => m.thumbnail);
    if (imageIndex !== -1) {
      image = blog.media.list[imageIndex].url;
    }
    const blogJsonLD = {
      "@context": "http://schema.org",
      "@type": "BlogPosting",
      "headline": blog.title,
      "alternativeHeadline": blog.metadescription,
      "mainEntityOfPage": {
        "@type": "WebPage",
        "@id": "https://www.nucamp.co/blog/" + blog.shortid,
      },
      image,
      "author": {
        "@type": "Person",
        "name": blog.author
      },
      "publisher": {
        "@type": "Organization",
        "name": "Nucamp Coding Bootcamp",
        "logo": {
          "@type": "ImageObject",
          "url": "https://www.nucamp.co/assets/imgs/nucamp-logo-full.jpg"
        }
      },
      "url": "https://www.nucamp.co/blog/" + blog.shortid,
      "datePublished": moment(blog.createdAt).toISOString(),
      "dateCreated": moment(blog.createdAt).toISOString(),
      "dateModified": blog.updatedAt ? moment(blog.updatedAt).toISOString() : moment(blog.createdAt).toISOString(),
      "description": blog.metadescription,
      "articleBody": blog.contextText
    }
    if (blog.h1.toLowerCase().includes("tech news in")) {
      blogJsonLD["@type"] = "NewsArticle";
    }
    return blogJsonLD;
  }

  // public JSONLDBreadcrum(blog) {
  //   let image = "https://www.nucamp.co/assets/imgs/reviews/badge-nucamp-number1.webp";
  //   let imageIndex = -1;
  //   if (blog?.media?.list) {
  //     imageIndex = blog.media.list.findIndex(m => m.thumbnail);
  //   }
  //   if (imageIndex !== -1) {
  //     image = blog.media.list[imageIndex].url;
  //   } else if (blog?.media?.list) {
  //     const firstImage = blog.media.list.find(m => m.type === "image");
  //     if (firstImage) image = firstImage.url;
  //   }
  //   return {
  //     "@context": "http://schema.org",
  //     "@type": "BreadcrumbList",
  //     "name": "Blog",
  //     "itemListElement": [
  //       {
  //         "@type": "ListItem",
  //         "position": 1,
  //         "item": {
  //           "@id": "https://www.nucamp.co/blog",
  //           "name": "Blogs",
  //         }
  //       },
  //       {
  //         "@type": "ListItem",
  //         "position": 2,
  //         "item": {
  //           "@id": "https://www.nucamp.co/blog/" + blog.shortid,
  //           "name": blog.title,
  //           "image": image
  //         }
  //       }
  //     ]
  //   }
  // }
}
